import { create } from 'zustand'
import type { JobDTO, ServerEvent } from '@shared/types'
import { ImageStatus, isJobActive } from '@shared/types'
import { api } from '@/lib/api'

interface JobState {
  jobs: Record<string, JobDTO>

  activeForSite: (siteId: string) => JobDTO | null
  applyEvent: (e: ServerEvent) => void
  load: (siteId?: string) => Promise<void>
  cancel: (jobId: string) => Promise<JobDTO>
}

interface SeenItems {
  completed: Set<string>
  failed: Set<string>
}

/**
 * Per-item outcomes observed on the stream, so the progress bar can move on the
 * first `image`/`saved` event instead of waiting for the next `job` snapshot.
 * Kept outside the store: it is bookkeeping, not render state.
 */
const seenItems = new Map<string, SeenItems>()

function noteItem(jobId: string, bucket: 'completed' | 'failed', imageId: string): SeenItems {
  let seen = seenItems.get(jobId)
  if (!seen) {
    seen = { completed: new Set(), failed: new Set() }
    seenItems.set(jobId, seen)
  }
  // A retried item switches buckets rather than being counted in both.
  seen.completed.delete(imageId)
  seen.failed.delete(imageId)
  seen[bucket].add(imageId)
  return seen
}

/**
 * Takes the higher of the server's counter and what we have actually seen, so a
 * `job` snapshot that predates the last few items never walks progress backwards.
 */
function withCounters(job: JobDTO, completedSeen: number, failedSeen: number): JobDTO {
  let failed = Math.max(job.failed, failedSeen)
  let completed = Math.max(job.completed, completedSeen)
  if (job.total > 0) {
    failed = Math.min(failed, job.total)
    completed = Math.min(completed, job.total - failed)
  }
  if (completed === job.completed && failed === job.failed) return job
  return { ...job, completed, failed }
}

function newer(a: JobDTO, b: JobDTO): JobDTO {
  return Date.parse(b.createdAt) > Date.parse(a.createdAt) ? b : a
}

export const useJobStore = create<JobState>((set, get) => {
  function upsert(job: JobDTO): void {
    set((state) => ({ jobs: { ...state.jobs, [job.id]: job } }))
  }

  /** Re-derives a live job's counters from the item events seen for it. */
  function reconcile(jobId: string): void {
    const job = get().jobs[jobId]
    const seen = seenItems.get(jobId)
    if (!job || !seen) return
    if (!isJobActive(job)) {
      // Finished: the server's final tally is the truth, and the bookkeeping for
      // a job that can no longer change is dead weight.
      seenItems.delete(jobId)
      return
    }
    const merged = withCounters(job, seen.completed.size, seen.failed.size)
    if (merged !== job) upsert(merged)
  }

  return {
    jobs: {},

    activeForSite: (siteId) => {
      let match: JobDTO | null = null
      for (const job of Object.values(get().jobs)) {
        if (job.siteId !== siteId || !isJobActive(job)) continue
        match = match ? newer(match, job) : job
      }
      return match
    },

    applyEvent: (event) => {
      switch (event.type) {
        case 'hello': {
          const next: Record<string, JobDTO> = {}
          // `hello` is the authoritative list of what is still running: anything
          // active we knew about but that is missing here finished while the
          // stream was down, so it is dropped rather than left spinning.
          for (const [id, job] of Object.entries(get().jobs)) {
            if (!isJobActive(job)) next[id] = job
          }
          for (const job of event.activeJobs) next[job.id] = job
          for (const jobId of [...seenItems.keys()]) {
            const job = next[jobId]
            if (!job || !isJobActive(job)) seenItems.delete(jobId)
          }
          set({ jobs: next })
          break
        }

        case 'job': {
          const previous = get().jobs[event.job.id]
          // An event that was in flight when the job finished must not resurrect it.
          if (previous && !isJobActive(previous) && isJobActive(event.job)) break
          upsert(event.job)
          reconcile(event.job.id)
          break
        }

        case 'image': {
          if (event.status === ImageStatus.Completed || event.status === ImageStatus.Failed) {
            const bucket = event.status === ImageStatus.Completed ? 'completed' : 'failed'
            noteItem(event.jobId, bucket, event.imageId)
            reconcile(event.jobId)
          }
          break
        }

        case 'saved': {
          noteItem(event.jobId, event.success ? 'completed' : 'failed', event.imageId)
          reconcile(event.jobId)
          break
        }

        case 'progress': {
          const job = get().jobs[event.jobId]
          if (!job || job.message === event.message) break
          upsert({ ...job, message: event.message })
          break
        }

        // Site totals belong to useSitesStore, and the keepalive carries nothing.
        case 'site':
        case 'ping':
          break

        default:
          // A newer server may add variants; an unknown one is not an error.
          break
      }
    },

    load: async (siteId) => {
      const params = new URLSearchParams({ active: 'true' })
      if (siteId) params.set('siteId', siteId)
      const jobs = await api.get<JobDTO[]>(`/jobs?${params.toString()}`)
      set((state) => {
        const next = { ...state.jobs }
        for (const job of jobs) next[job.id] = job
        return { jobs: next }
      })
    },

    cancel: async (jobId) => {
      const job = await api.post<JobDTO>(`/jobs/${jobId}/cancel`)
      upsert(job)
      reconcile(jobId)
      return job
    }
  }
})
